import { ContentService } from '@blk/cwp-ng-framework';
import { DZ_COLOR_BLK_WHITE, DZ_COLOR_YELLOW_100 } from '@blk/daisy/core/styles';
import { Alignment, ColumnComposition, ColumnCompositions, ImageCellRenderer } from '@blk/simba';
import { CellData, CellRow, ColumnConfig, ComponentData, TabData, TableData } from '@ddt-web/data-service';
import { PartnerHoldingsAlertsService } from '@ddt-web/partner-integrations';
import * as cloneDeep from 'lodash/cloneDeep';
import * as get from 'lodash/get';

import { SvgImage } from '../static-pages';
import { TableUtils } from '../utils/table-utils';
import { DefaultColumnComposition } from './default-column-composition';
import { TableCompositionHelper } from './table-composition-helper';

export class HoldingsColumnComposition extends DefaultColumnComposition {
	public getHoldingsCompositions(
		tabData: TabData,
		initialTabData?: TabData,
		securitiesTab?: TabData,
		contentService?: ContentService,
		showColorForNumber: boolean = false,
		shouldReduceSpacing: boolean = false,
		isWithIdentifiers: boolean = true,
		compareTableCustomizations: boolean = false,
		selectedPortfolioIndex?: number

	): Array<ColumnCompositions> {
		const tableData: TableData = get(tabData, 'tableData[0]');
		const partnerHoldingsAlertsService = new PartnerHoldingsAlertsService(contentService);
		let substitutionAlerts = [];
		if (securitiesTab && securitiesTab.tableData.length && !securitiesTab.error) {
			substitutionAlerts = partnerHoldingsAlertsService.getPortfolioSubstitutionAlerts(securitiesTab);
		}
		const assetClasses: Array<CellData> = tableData.headersY.filter((header: CellData) => header.config && header.config.visible);
		const assetClassesWeights: CellData[][] = tableData.cells.filter((row: Array<CellData>) => row[0].config && row[0].config.visible);
		const tableHeaders: CellData[][] = assetClasses.map((assetClass: CellData, index: number) => {
			return [assetClass, ...assetClassesWeights[index]];
		});
		const tableRows: CellData[][][] = assetClasses.map((assetClass: CellData, index: number) => {
			return assetClass.children.map((cellData: CellData, childIndex: number) => {
				return [
					//The "getTransformedTab" transforms the data in such a way that the columnId for all the columns are changed to `cell`. 
					// Hence, changing the columnId of tickers back to "ticker" in order to uniquely identify it. 
					...(isWithIdentifiers ? [new CellData(new ColumnConfig(null, null, null, 'ticker'), cellData.attribute.ticker, cellData.attribute.ticker)] : []),
					cellData,
					...assetClassesWeights[index].map(portfolio => portfolio.children[childIndex])
				];
			});
		});

		const noOfPortfolios = tableHeaders[0].length - 1;

		const headerColumnProportions = TableCompositionHelper.getHoldingsColumnProportions(noOfPortfolios, true, shouldReduceSpacing);
		const rowColumnProportions = TableCompositionHelper.getHoldingsColumnProportions(noOfPortfolios, !isWithIdentifiers, shouldReduceSpacing);

		// adjust column proportions for first 2 cols for comparison table
		if (compareTableCustomizations) {
			rowColumnProportions[0] -= 3;
			rowColumnProportions[1] += 3;
		}
		return tableHeaders.map((header: CellData[], headerIndex: number) => {
			const headers: ColumnComposition[] = tableHeaders[headerIndex].map((cell: CellData, index: number) => {
				return new ColumnComposition({
					cells: cell,
					widthProportion: headerColumnProportions[index],
					textClasses: ['text-bold', this.textSize],
					horizontalAlignment: index > 0 ? Alignment.Horizontal.Right : Alignment.Horizontal.Left,
					verticalAlignment: Alignment.Vertical.Middle,
					boxModelConstraint: {
						paddingRight: index > 0 ? TableCompositionHelper.getTableColumnBoxModelConstraints(noOfPortfolios, shouldReduceSpacing) : 0
					},
					cellBoxModelConstraint: compareTableCustomizations ? {
						backgroundColor: index === selectedPortfolioIndex ? DZ_COLOR_YELLOW_100 : DZ_COLOR_BLK_WHITE,
						useFullHeight: true
					} : {}
				});
			});

			const body: ColumnComposition[][] = [];
			const startDataIndex = isWithIdentifiers ? 1 : 0;
			tableRows[headerIndex].forEach((row: CellData[]) => {
				const rowCompositions: ColumnComposition[] = row.map(
					(cell: CellData, cellIndex: number) =>
						new ColumnComposition({
							cells: cell,
							widthProportion: rowColumnProportions[cellIndex],
							textClasses: this.getTextClass(cell, showColorForNumber),
							horizontalAlignment: cellIndex > startDataIndex ? Alignment.Horizontal.Right : Alignment.Horizontal.Left,
							verticalAlignment: Alignment.Vertical.Middle,
							boxModelConstraint: {
								paddingRight: cellIndex > startDataIndex ? TableCompositionHelper.getTableColumnBoxModelConstraints(noOfPortfolios, shouldReduceSpacing) : 0
							},
							cellBoxModelConstraint: compareTableCustomizations ? {
								backgroundColor: cellIndex === selectedPortfolioIndex + 1 ? DZ_COLOR_YELLOW_100 : DZ_COLOR_BLK_WHITE,
								paddingBottom: cellIndex === 1 ? 5 : 0,
								useFullHeight: true
							} : {}
						})
				);
				body.push(rowCompositions);

				const alertsRowCompositions: Array<ColumnComposition[]> = partnerHoldingsAlertsService.generateAlertRowsForPDF(
					row[0].formattedValue,
					substitutionAlerts,
					this.textSize,
					rowColumnProportions,
					noOfPortfolios
				);
				if (alertsRowCompositions) {
					alertsRowCompositions.forEach(alertsRowComposition => body.push(alertsRowComposition));
				}
			});

			return new ColumnCompositions(headers, body);
		});
	}

	public getSnapShotHeadingCompositions(tabData: TabData, columnProportions: Array<number>): Array<ColumnComposition> {
		const tableData: TableData = get(tabData, 'tableData[0]');

		return [new CellData(null, ''), ...tableData.headersX].map(
			(header: CellData, index: number) =>
				new ColumnComposition({
					cells: [header],
					widthProportion: columnProportions[index],
					textClasses: ['text-bold', this.textSize],
					horizontalAlignment: !!index ? Alignment.Horizontal.Right : Alignment.Horizontal.Left,
					verticalAlignment: Alignment.Vertical.Bottom,
					boxModelConstraint: { paddingLeft: 10 }
				})
		);
	}

	public getSnapShotBenchmarkBannerCompositions(tabData: TabData, columnProportions: Array<number>, benchmarkIndex: number, marginLeft: number = 30): Array<ColumnComposition> {
		const tableData: TableData = get(tabData, 'tableData[0]');

		return [new CellData(null, ''), ...tableData.headersX].map(
			(header: CellData, index: number) => {
				if (benchmarkIndex === index - 1) {
					return new ColumnComposition({
						renderer: new ImageCellRenderer(
							new SvgImage('BENCHMARK').getStaticContent(),
							columnProportions[index],
							Alignment.Horizontal.Right,
							Alignment.Vertical.Top,
							{ useFullHeight: true },
							{ marginLeft }
						)
					});
				} else {
					return new ColumnComposition({
						cells: [new CellData(null, '')],
						widthProportion: columnProportions[index],
						textClasses: [this.textSize],
						horizontalAlignment: Alignment.Horizontal.Left,
						verticalAlignment: Alignment.Vertical.Middle,
						boxModelConstraint: this.firstColumnBoxModel
					});
				}
			}

		);
	}

	public getSnapShotPortfolioTypeCompositions(data: ComponentData, contentSvc: ContentService, columnProportions: Array<number>): Array<ColumnComposition> {
		const designations = this.populateDesignations(data, contentSvc);
		return [new CellData(null, ''), ...designations.portfolioIdKeys].map((key: string, index: number) => {
			return new ColumnComposition({
				cells: [designations.designationsMap[key] ? designations.designationsMap[key] : new CellData(null, '')],
				widthProportion: columnProportions[index],
				textClasses: [this.textSize],
				horizontalAlignment: index > 0 ? Alignment.Horizontal.Right : Alignment.Horizontal.Left,
				verticalAlignment: Alignment.Vertical.Bottom,
				boxModelConstraint: this.boxModel
			});
		});
	}

	public populateDesignations(data: ComponentData, contentSvc: ContentService): any {
		const designationsMap: any = {};
		const portfolioIdKeys = [];

		data.portfolioData.forEach((portfolioData: CellRow) => {
			const designationVal = cloneDeep(portfolioData.field['outputDesignation']);
			const portfolioKey = portfolioData.field['id'];
			const portfolioType = portfolioData.field['portfolioType'];
			portfolioIdKeys.push(portfolioKey.formattedValue);

			const translatedDesignationValue = TableUtils.getDesignationTranslatedValues(designationVal.formattedValue, portfolioType.formattedValue, contentSvc);
			designationVal.formattedValue = translatedDesignationValue.toUpperCase();
			designationVal.rawValue = translatedDesignationValue;
			designationsMap[portfolioKey.formattedValue] = designationVal;
		});
		return { designationsMap, portfolioIdKeys };
	}

	public getTextClass(cell: CellData, showColorForNumber: boolean): string[] {
		const textClass: string[] = [this.textSize];
		if (showColorForNumber) {
			if (cell.config?.id !== 'ticker') {
				if (get(cell, 'rawValue') < 0) {
					textClass.push('text-st-table-red');
				} else if (get(cell, 'rawValue') > 0) {
					textClass.push('text-st-table-green');
				}
			}
		}

		return textClass;
	}
}
